import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { LayoutGrid, Loader2, Trash2, Pencil, Clock, Plus, Layers } from 'lucide-react';
import { AppNavbar } from '../components/AppNavbar';
import { MobileTabBar } from '../components/MobileTabBar';

export default function MyCarouselsPage() {
  const navigate = useNavigate();
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get('/carousel/compositions');
        setComps(res.data.compositions || res.data || []);
      } catch {
        toast.error('Failed to load saved carousels');
      }
      setLoading(false);
    })();
  }, []);

  const handleDelete = async (compId) => {
    if (!window.confirm('Delete this carousel?')) return;
    try {
      await apiClient.delete(`/carousel/compositions/${compId}`);
      setComps(prev => prev.filter(c => c.composition_id !== compId));
      toast.success('Carousel deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0" data-testid="my-carousels-page">
      <AppNavbar />
      <div className="border-b border-border/50 px-4 py-3 flex items-center gap-3">
        <LayoutGrid className="h-5 w-5 text-amber-500" />
        <h1 className="text-lg font-semibold">My Carousels</h1>
        <div className="flex-1" />
        <Button size="sm" onClick={() => navigate('/carousel-editor')} data-testid="create-new-carousel-btn">
          <Plus className="h-4 w-4 mr-1" /> Create New Carousel
        </Button>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : comps.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <LayoutGrid className="h-12 w-12 mx-auto mb-4 opacity-20" />
            <p className="text-lg mb-2">No saved carousels yet</p>
            <p className="text-sm mb-4">Create your first carousel from a template</p>
            <Button onClick={() => navigate('/carousel-editor')}>
              <Plus className="h-4 w-4 mr-1" /> Create Carousel
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {comps.map((comp) => {
              const slides = comp.slides || [];
              const firstSlide = slides[0];
              return (
                <div key={comp.composition_id}
                  className="border border-border/50 rounded-xl overflow-hidden bg-card hover:shadow-lg transition-all group cursor-pointer"
                  onClick={() => navigate(`/carousel-editor?id=${comp.composition_id}`)}
                  data-testid={`carousel-card-${comp.composition_id}`}>
                  <div className="relative aspect-square bg-muted/30 max-h-48">
                    {firstSlide?.background_image ? (
                      <img src={firstSlide.background_image} alt={comp.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: firstSlide?.background_color || '#1a1a2e' }}>
                        <LayoutGrid className="h-8 w-8 opacity-20" />
                      </div>
                    )}
                    {/* Slide count badge */}
                    <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
                      {slides.length} slides
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="font-medium text-sm truncate mb-1">{comp.name || 'Untitled Carousel'}</h3>
                    <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                      <span className="flex items-center gap-1"><Layers className="h-3 w-3" /> {slides.length} slides</span>
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {comp.aspect_ratio || '1:1'}</span>
                    </div>
                    <div className="flex gap-1.5 mt-2">
                      <Button variant="outline" size="sm" className="flex-1 text-xs"
                        onClick={(e) => { e.stopPropagation(); navigate(`/carousel-editor?id=${comp.composition_id}`); }}>
                        <Pencil className="h-3 w-3 mr-1" /> Edit
                      </Button>
                      <Button variant="outline" size="sm" className="text-xs text-destructive"
                        onClick={(e) => { e.stopPropagation(); handleDelete(comp.composition_id); }}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <MobileTabBar />
    </div>
  );
}
